import "./Brands.scss";

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { useBadgeCounts } from "../../contexts/BadgeCountsContext";
import { safeDate } from "../../utils/safeDate";
import {
  PLATFORM_LABELS,
  TIER_LABELS,
  approveAllIds,
  buildDecidePayload,
  compactCount,
  handleLink,
  safeHttps,
  sortBrands,
  wordLabel,
} from "./brandHelpers";

// ZONA for brands: who gets brand access, which batches go out, and which stories count.
const TABS = [
  { key: "brands", label: "Brands" },
  { key: "batches", label: "Batches" },
  { key: "stories", label: "Stories" },
];

const STATUS_WORDS = { pending: "Waiting", approved: "Approved", declined: "Declined", removed: "Removed" };

const errMessage = (err, fallback) => err?.response?.data?.message || fallback;

// Busy ids as a Set, so one finished request doesn't free another row's buttons.
function useBusy() {
  const [busy, setBusy] = useState(() => new Set());
  const start = useCallback((ids) => setBusy((b) => new Set([...b, ...ids])), []);
  const end = useCallback((ids) => setBusy((b) => {
    const n = new Set(b);
    ids.forEach((id) => n.delete(id));
    return n;
  }), []);
  return [busy, start, end];
}

// Loads a list once with a spinner; later reloads run quietly so the page keeps its place.
function useList(path, pick, fallback) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get(path);
      setRows(pick(data));
      setError("");
    } catch (err) {
      if (quiet) toast.error(errMessage(err, fallback));
      else setError(errMessage(err, fallback));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [path, pick, fallback]);
  useEffect(() => {
    load();
  }, [load]);
  return { rows, setRows, loading, error, load };
}

const pickBrands = (d) => sortBrands(d?.brands);
const pickBatches = (d) => d?.batches || [];
const pickStories = (d) => d?.stories || [];

function Brands() {
  const [tab, setTab] = useState("brands");
  // The exact story link opened per invite; kept here so switching tabs doesn't lose it.
  const [opened, setOpened] = useState({});

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="brands">
            <div className="brands__head">
              <h1>ZONA for brands</h1>
              <p className="brands__sub">
                Approve brand accounts, check each list a brand sends before anyone is invited, and
                confirm the stories members post after the event.
              </p>
            </div>

            <div className="brands__tabs" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  className={`brands__tab${tab === t.key ? " brands__tab--active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "brands" && <BrandsTab />}
            {tab === "batches" && <BatchesTab />}
            {tab === "stories" && <StoriesTab opened={opened} setOpened={setOpened} />}
          </section>
        </article>
      </div>
    </main>
  );
}

function BrandsTab() {
  const { rows, setRows, loading, error } = useList("/brand/admin/requests", pickBrands, "Couldn't load brand requests.");
  const [busy, startBusy, endBusy] = useBusy();
  const { refreshCounts } = useBadgeCounts();

  const decide = async (row, status) => {
    const who = row.brand_account?.company || row.email;
    if (status === "removed" && !window.confirm(`Remove brand access for ${who}?`)) return;
    startBusy([row._id]);
    try {
      await api.patch(`/brand/admin/${row._id}`, { status });
      // Update this row in place so the list keeps its order and scroll.
      setRows((rs) => rs.map((r) => (r._id === row._id
        ? { ...r, brand_account: { ...r.brand_account, status, decided_at: new Date().toISOString() } }
        : r)));
      toast.success(`${who}: ${STATUS_WORDS[status].toLowerCase()}`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save that. Try again."));
    } finally {
      endBusy([row._id]);
      // Success or not, the badge rereads what is left.
      refreshCounts?.();
    }
  };

  if (loading) return <div className="brands__empty">Loading...</div>;
  if (error) return <div className="brands__error">{error}</div>;
  if (!rows.length) return <div className="brands__empty">No brand requests yet.</div>;

  return (
    <div className="brands__table-wrap">
      <table className="brands__table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Requested</th>
            <th>Decided</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const a = r.brand_account || {};
            const isBusy = busy.has(r._id);
            return (
              <tr key={r._id}>
                <td className="brands__name">{a.company || "No company given"}</td>
                <td>{[r.firstName, r.lastName].filter(Boolean).join(" ")}</td>
                <td>{r.email}</td>
                <td>{STATUS_WORDS[a.status] || a.status}</td>
                <td className="brands__muted">{safeDate(a.requested_at)}</td>
                <td className="brands__muted">
                  {safeDate(a.decided_at)}
                  {a.decided_by ? <div>by {a.decided_by}</div> : null}
                </td>
                <td className="brands__actions">
                  {a.status !== "approved" && (
                    <button className="brands__primary" disabled={isBusy} onClick={() => decide(r, "approved")}>
                      Approve
                    </button>
                  )}
                  {a.status === "pending" && (
                    <button className="brands__secondary" disabled={isBusy} onClick={() => decide(r, "declined")}>
                      Decline
                    </button>
                  )}
                  {a.status === "approved" && (
                    <button className="brands__secondary" disabled={isBusy} onClick={() => decide(r, "removed")}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BatchesTab() {
  const { rows: batches, setRows: setBatches, loading, error, load } = useList("/brand/admin/batches", pickBatches, "Couldn't load batches.");
  const [busy, startBusy, endBusy] = useBusy();
  const { refreshCounts } = useBadgeCounts();

  const decide = async (batch, choice) => {
    const body = buildDecidePayload(batch.invites, choice);
    const ids = [...body.approve, ...body.reject];
    if (!ids.length) return;
    startBusy(ids);
    try {
      const { data } = await api.post(`/brand/admin/events/${batch.id}/decide`, body);
      const approved = Number(data?.approved) || 0;
      const rejected = Number(data?.not_approved) || 0;
      const skipped = body.approve.length - approved + (body.reject.length - rejected);
      const parts = [];
      if (body.approve.length) parts.push(`${approved} approved`);
      if (body.reject.length) parts.push(`${rejected} not approved`);
      toast.success(parts.join(", "));
      if (skipped > 0) toast.info(`${skipped} couldn't be changed. They may have left the pool or been decided already.`);
      // Drop the decided rows in place, then reload quietly to match the server.
      const done = new Set(ids);
      setBatches((bs) => bs
        .map((x) => (x.id === batch.id ? { ...x, invites: x.invites.filter((i) => !done.has(String(i.user_id))) } : x))
        .filter((x) => x.invites.length));
      load({ quiet: true });
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save that. Try again."));
    } finally {
      endBusy(ids);
      // Success or not, the badge rereads what is left.
      refreshCounts?.();
    }
  };

  const approveAll = (b) => {
    const ids = approveAllIds(b.invites);
    if (!ids.length) return;
    const who = `${ids.length} ${ids.length === 1 ? "person" : "people"}`;
    if (!window.confirm(`Invite ${who} to ${b.name}? This can't be undone.`)) return;
    decide(b, { approve: ids });
  };

  if (loading) return <div className="brands__empty">Loading...</div>;
  if (error) return <div className="brands__error">{error}</div>;
  if (!batches.length) return <div className="brands__empty">No batches waiting.</div>;

  return (
    <>
      <p className="brands__note">
        Approving invites a member but doesn't message them yet. Unavailable people left the pool
        after the brand picked them, so they can only be rejected.
      </p>
      {batches.map((b) => {
        const available = approveAllIds(b.invites);
        const anyBusy = b.invites.some((i) => busy.has(String(i.user_id)));
        return (
          <div key={b.id} className="brands__batch">
            <div className="brands__batch-head">
              <div>
                <div className="brands__batch-title">
                  {b.company || "Unknown company"}: {b.name}
                </div>
                <div className="brands__muted">
                  {[
                    TIER_LABELS[b.tier] || wordLabel(b.tier),
                    safeDate(b.starts_at, "EEE MMM d, h:mm a"),
                    b.venue_name,
                    b.city,
                    `${b.invites.length} ${b.invites.length === 1 ? "person" : "people"}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <button
                className="brands__primary"
                disabled={!available.length || anyBusy}
                onClick={() => approveAll(b)}
              >
                {anyBusy ? "Saving..." : "Approve all"}
              </button>
            </div>

            <div className="brands__table-wrap">
              <table className="brands__table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>City</th>
                    <th>Age</th>
                    <th>Categories</th>
                    <th>Followers</th>
                    <th>Handles</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {b.invites.map((i) => (
                    <PersonRow
                      key={i.id}
                      invite={i}
                      busy={busy.has(String(i.user_id))}
                      onApprove={() => decide(b, { approve: [i.user_id] })}
                      onReject={() => decide(b, { reject: [i.user_id] })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

function PersonRow({ invite, busy, onApprove, onReject }) {
  const m = invite.member;
  if (invite.unavailable || !m) {
    return (
      <tr className="brands__row--off">
        <td><div className="brands__photo brands__photo--empty" /></td>
        <td colSpan={6}>Unavailable</td>
        <td className="brands__actions">
          <button className="brands__secondary" disabled={busy} onClick={onReject}>Reject</button>
        </td>
      </tr>
    );
  }
  const followers = Object.entries(m.follower_counts || {})
    .map(([p, n]) => `${PLATFORM_LABELS[p] || p} ${compactCount(n)}`)
    .join(", ");
  const links = ["instagram", "tiktok"].map((p) => handleLink(p, m.handles?.[p])).filter(Boolean);
  const photo = safeHttps(m.photo_url);
  return (
    <tr>
      <td>
        {photo ? <img className="brands__photo" src={photo} alt="" /> : <div className="brands__photo brands__photo--empty" />}
      </td>
      <td>
        <div className="brands__name">{m.first_name || "No name"}</div>
        {invite.plus_ones ? <div className="brands__muted">+{invite.plus_ones}</div> : null}
      </td>
      <td>{m.city ? wordLabel(m.city) : ""}</td>
      <td>{m.age ?? ""}</td>
      <td>{[...(m.categories || []), ...(m.creator_types || [])].map(wordLabel).join(", ")}</td>
      <td>{followers}</td>
      <td>
        {links.map((l) => (
          <div key={l.label}>
            {l.url ? (
              <a className="brands__link" href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
            ) : (
              <span className="brands__muted">{l.label}</span>
            )}
          </div>
        ))}
      </td>
      <td className="brands__actions">
        <button className="brands__primary" disabled={busy} onClick={onApprove}>Approve</button>
        <button className="brands__secondary" disabled={busy} onClick={onReject}>Reject</button>
      </td>
    </tr>
  );
}

function StoriesTab({ opened, setOpened }) {
  const { rows: stories, setRows: setStories, loading, error, load } = useList("/brand/admin/stories", pickStories, "Couldn't load stories.");
  const [busy, startBusy, endBusy] = useBusy();
  const { refreshCounts } = useBadgeCounts();
  const markOpened = (id, url) => setOpened((o) => ({ ...o, [id]: url }));

  const decide = async (s, status) => {
    const body = status === "confirmed" ? { status, url: opened[s.id] } : { status };
    if (status === "confirmed" && !body.url) return;
    startBusy([s.id]);
    try {
      await api.post(`/brand/admin/invites/${s.id}/post`, body);
      toast.success(status === "confirmed" ? "Story confirmed" : "Marked missed");
      setStories((rs) => rs.filter((r) => r.id !== s.id));
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("This one changed or was already decided. Open the link again.");
        setOpened((o) => ({ ...o, [s.id]: undefined }));
        load({ quiet: true });
      } else {
        toast.error(errMessage(err, "Couldn't save that. Try again."));
      }
    } finally {
      endBusy([s.id]);
      // Success or not, the badge rereads what is left.
      refreshCounts?.();
    }
  };

  if (loading) return <div className="brands__empty">Loading...</div>;
  if (error) return <div className="brands__error">{error}</div>;
  if (!stories.length) return <div className="brands__empty">No stories to check.</div>;

  return (
    <>
      <p className="brands__note">
        Open the link, check the brand tag and the ad disclosure, then mark it. Confirmed needs the
        link opened first. People with no link yet can only be marked missed.
      </p>
      <div className="brands__table-wrap">
        <table className="brands__table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Event</th>
              <th>Checked in</th>
              <th>Story</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {stories.map((s) => {
              const url = s.post?.status === "submitted" ? safeHttps(s.post?.url) : null;
              const seen = url && opened[s.id] === url;
              const isBusy = busy.has(s.id);
              return (
                <tr key={s.id}>
                  <td className="brands__name">{s.member?.first_name || (s.unavailable ? "Unavailable" : "No name")}</td>
                  <td>
                    {s.event?.name}
                    <div className="brands__muted">{safeDate(s.event?.starts_at, "EEE MMM d")}</div>
                  </td>
                  <td className="brands__muted">{safeDate(s.checked_in_at)}</td>
                  <td>
                    {url ? (
                      <a
                        className="brands__link"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => markOpened(s.id, url)}
                        onAuxClick={() => markOpened(s.id, url)}
                        onContextMenu={() => markOpened(s.id, url)}
                      >
                        Open story
                      </a>
                    ) : (
                      <span className="brands__muted">No link yet</span>
                    )}
                  </td>
                  <td className="brands__actions">
                    {url && (
                      <button
                        className="brands__primary"
                        disabled={!seen || isBusy}
                        title={seen ? "" : "Open the story first"}
                        onClick={() => decide(s, "confirmed")}
                      >
                        Confirmed
                      </button>
                    )}
                    <button className="brands__secondary" disabled={isBusy} onClick={() => decide(s, "missed")}>
                      Missed
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default Brands;
